import { BackendType, getBackendConfig, isLaravelBackend } from '../config/backend';

import type { IAuthAdapter } from './auth/IAuthAdapter';
import { GASAuthAdapter } from './auth/GASAuthAdapter';
import { LaravelAuthAdapter } from './auth/LaravelAuthAdapter';

import type { IMasterDataAdapter } from './masterData/IMasterDataAdapter';
import { GASMasterDataAdapter } from './masterData/GASMasterDataAdapter';
import { LaravelMasterDataAdapter } from './masterData/LaravelMasterDataAdapter';

import type { IComplaintAdapter } from './complaint/IComplaintAdapter';
import { GASComplaintAdapter } from './complaint/GASComplaintAdapter';
import { LaravelComplaintAdapter } from './complaint/LaravelComplaintAdapter';

import type { IWorkOrderAdapter } from './workOrder/IWorkOrderAdapter';
import { GASWorkOrderAdapter } from './workOrder/GASWorkOrderAdapter';
import { LaravelWorkOrderAdapter } from './workOrder/LaravelWorkOrderAdapter';

import type { IVendorAdapter } from './vendor/IVendorAdapter';
import { GASVendorAdapter } from './vendor/GASVendorAdapter';
import { LaravelVendorAdapter } from './vendor/LaravelVendorAdapter';

import type { ITechnicianAdapter } from './technician/ITechnicianAdapter';
import { GASTEchnicianAdapter } from './technician/GASTEchnicianAdapter';
import { LaravelTechnicianAdapter } from './technician/LaravelTechnicianAdapter';

import type { IInspectionAdapter } from './inspection/IInspectionAdapter';
import { GASInspectionAdapter } from './inspection/GASInspectionAdapter';
import { LaravelInspectionAdapter } from './inspection/LaravelInspectionAdapter';

import type { INotificationAdapter } from './notification/INotificationAdapter';
import { GASNotificationAdapter } from './notification/GASNotificationAdapter';

import type { IBillingAdapter } from './billing/IBillingAdapter';
import { GASBillingAdapter } from './billing/GASBillingAdapter';
import { LaravelBillingAdapter } from './billing/LaravelBillingAdapter';

import type { IStorageAdapter } from './attachment/IStorageAdapter';
import { GASDriveAdapter } from './attachment/GASDriveAdapter';
import { LaravelStorageAdapter } from './attachment/LaravelStorageAdapter';

import type { IReportsAdapter } from './reports/IReportsAdapter';
import { GASReportsAdapter } from './reports/GASReportsAdapter';
import { LaravelReportsAdapter } from './reports/LaravelReportsAdapter';

import type { IAdminAdapter } from './admin/IAdminAdapter';
import { GASAdminAdapter } from './admin/GASAdminAdapter';
import { LaravelAdminAdapter } from './admin/LaravelAdminAdapter';

import type { IAuditAdapter } from './audit/IAuditAdapter';
import { GASAuditAdapter } from './audit/GASAuditAdapter';
import { LaravelAuditAdapter } from './audit/LaravelAuditAdapter';

const instances = new Map<string, unknown>();

function singleton<T>(key: string, create: () => T): T {
  if (!instances.has(key)) {
    instances.set(key, create());
  }
  return instances.get(key) as T;
}

/**
 * Central environment-swappable adapter factory.
 *
 * Frontend pages and services resolve their backend through this single factory,
 * so switching `VITE_BACKEND_TYPE` from `GAS` to `LARAVEL` swaps every adapter
 * without touching UI or business logic.
 */
export class AdapterFactory {
  static getBackendType(): BackendType {
    return getBackendConfig().type;
  }

  static getAuthAdapter(): IAuthAdapter {
    return singleton<IAuthAdapter>('auth', () =>
      isLaravelBackend() ? new LaravelAuthAdapter() : new GASAuthAdapter()
    );
  }

  static getMasterDataAdapter(): IMasterDataAdapter {
    return singleton<IMasterDataAdapter>('masterData', () =>
      isLaravelBackend() ? new LaravelMasterDataAdapter() : new GASMasterDataAdapter()
    );
  }

  static getComplaintAdapter(): IComplaintAdapter {
    return singleton<IComplaintAdapter>('complaint', () =>
      isLaravelBackend() ? new LaravelComplaintAdapter() : new GASComplaintAdapter()
    );
  }

  static getWorkOrderAdapter(): IWorkOrderAdapter {
    return singleton<IWorkOrderAdapter>('workOrder', () =>
      isLaravelBackend() ? new LaravelWorkOrderAdapter() : new GASWorkOrderAdapter()
    );
  }

  static getVendorAdapter(): IVendorAdapter {
    return singleton<IVendorAdapter>('vendor', () =>
      isLaravelBackend() ? new LaravelVendorAdapter() : new GASVendorAdapter()
    );
  }

  static getTechnicianAdapter(): ITechnicianAdapter {
    return singleton<ITechnicianAdapter>('technician', () =>
      isLaravelBackend() ? new LaravelTechnicianAdapter() : new GASTEchnicianAdapter()
    );
  }

  static getInspectionAdapter(): IInspectionAdapter {
    return singleton<IInspectionAdapter>('inspection', () =>
      isLaravelBackend() ? new LaravelInspectionAdapter() : new GASInspectionAdapter()
    );
  }

  static getNotificationAdapter(): INotificationAdapter {
    return singleton<INotificationAdapter>('notification', () => new GASNotificationAdapter());
  }

  static getBillingAdapter(): IBillingAdapter {
    return singleton<IBillingAdapter>('billing', () =>
      isLaravelBackend() ? new LaravelBillingAdapter() : new GASBillingAdapter()
    );
  }

  static getAttachmentAdapter(): IStorageAdapter {
    return singleton<IStorageAdapter>('attachment', () =>
      isLaravelBackend() ? new LaravelStorageAdapter() : new GASDriveAdapter()
    );
  }

  static getStorageAdapter(): IStorageAdapter {
    return this.getAttachmentAdapter();
  }

  static getReportsAdapter(): IReportsAdapter {
    return singleton<IReportsAdapter>('reports', () =>
      isLaravelBackend() ? new LaravelReportsAdapter() : new GASReportsAdapter()
    );
  }

  static getAdminAdapter(): IAdminAdapter {
    return singleton<IAdminAdapter>('admin', () =>
      isLaravelBackend() ? new LaravelAdminAdapter() : new GASAdminAdapter()
    );
  }

  static getAuditAdapter(): IAuditAdapter {
    return singleton<IAuditAdapter>('audit', () =>
      isLaravelBackend() ? new LaravelAuditAdapter() : new GASAuditAdapter()
    );
  }

  static reset(): void {
    instances.clear();
  }
}
